import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import TasksPage from './TasksPage';
import LoginPage from './LoginPage';
import TradersPage from './TradersPage';
import SettingsModal from './SettingsModal';
import TradingPage from './TradingPage';


function App() {
  const [network, setNetwork] = useState('Solana');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [authenticated, setAuthenticated] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [solUsd, setSolUsd] = useState(null);
  const [openWallet, setOpenWallet] = useState(null);
  const [positions, setPositions] = useState([]);
  const [symbolByMint, setSymbolByMint] = useState({});
  const [logs, setLogs] = useState([]);
  const [globalRpc, setGlobalRpc] = useState('—');
  const [pendingAdded, setPendingAdded] = useState({});



  useEffect(() => {
    const loadRpc = async () => {
      try {
        const r = await fetch('/api-auth/rpc-current', { credentials: 'include' });
        const j = r.ok ? await r.json() : null;
        const cleanRpc = (u) => {
          try {
            return new URL(u).origin + '/';
          } catch {
            return '—';
          }
        };

        setGlobalRpc(cleanRpc(j?.currentRpc || ''));
      } catch {
        setGlobalRpc('—');
      }
    };

    loadRpc();
    const id = setInterval(loadRpc, 10000);
    return () => clearInterval(id);
  }, []);


  const refreshRpc = async () => {
    try {
      const r = await fetch('/api-auth/rpc-current', { credentials: 'include' });
      const j = r.ok ? await r.json() : null;
      setGlobalRpc(j?.currentRpc || '—');
    } catch {
      setGlobalRpc('—');
    }
  };


  useEffect(() => {
    let stop = false;
    const fetchPrice = () =>
    fetch('/api-auth/price/sol-usd')
      .then(r => r.json())
      .then(j => { if (!stop) setSolUsd(typeof j?.usd === 'number' ? j.usd : null); })
      .catch(() => { if (!stop) setSolUsd(null); });
    fetchPrice();
    const id = setInterval(fetchPrice, 30000);
    return () => { stop = true; clearInterval(id); };
  }, []);


  async function fetchBalance(name) {
    try {
      const r = await fetch(`/api-auth/balance/${encodeURIComponent(name)}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      return (typeof j.sol === 'number') ? j.sol : null;
    } catch {
      return null;
    }
  }


  async function fetchCurrentReserve(name) {
    try {
      const r = await fetch(`/api-auth/api/current-reserve/${encodeURIComponent(name)}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      return (typeof j.current_reserve === 'number') ? j.current_reserve : null;
    } catch {
      return null;
    }
  }


async function fetchBotStatus(name) {
  try {
    const r = await fetch(`/api-auth/bot-status/${encodeURIComponent(name)}`, {
      credentials: 'include'
    });
    const j = r.ok ? await r.json() : null;
    return j?.running === true;
  } catch {
    return false;
  }
}


async function updateBalancesOnce(list) {
  const updated = await Promise.all(
    (list || []).map(async (w) => {
      const name = w?.walletName;
      if (!name) return w;

      const sol = await fetchBalance(name);
      const running = await fetchBotStatus(name);
      const currentReserve = await fetchCurrentReserve(name); 

      return {
        ...w,
        balanceSol: sol,
        running,
        reserveAmount: currentReserve ?? w.reserveAmount
      };
    })
  );
  setWallets(updated);
}


function normalizeMint(m) {
  return (m || '').toString();
}

function groupMyTradesByMint(trades) {
  const out = {};
  for (const t of trades) {
    if (!t?.me) continue; // ТОЛЬКО мои сделки
    const mint =
      t.mint ||
      t.baseMint ||
      (Array.isArray(t.mints) ? t.mints[0] : null);
    if (!mint) continue;
    const key = normalizeMint(mint);
    if (!out[key]) out[key] = [];
    out[key].push(t);
  }
  // сортировка по времени (FIFO основа)
  for (const mint in out) {
    out[mint].sort(
      (a, b) =>
        Number(a.time || a.timestamp || 0) -
        Number(b.time || b.timestamp || 0)
    );
  }

  return out;
}



function calcOpenPositionsFIFO(grouped) {
  return Object.entries(grouped).reduce((acc, [mint, trades]) => {
    const queue = [];
    let lastBuyTime = 0;
    for (const t of trades) {
      const side = (t.side || '').toLowerCase();
      const amt = Number(t.baseAmount ?? t.amount ?? 0);
      const sol = Number(t.quoteAmount ?? 0);
      const time = Number(t.time || t.timestamp || 0);
      if (side === 'buy' && amt > 0) {
        queue.push({ amt, sol, time });
        if (time > lastBuyTime) lastBuyTime = time;
      }

      if (side === 'sell' && amt > 0) {
        let left = amt;
        while (left > 0 && queue.length) {
          const q = queue[0];
          if (q.amt <= left) {
            left -= q.amt;
            queue.shift();
          } else {
            q.amt -= left;
            left = 0;
          }
        }
      }
    }

    const amount = queue.reduce((s, q) => s + q.amt, 0);
    if (amount <= 0) return acc;
    const buySol = queue.reduce((s, q) => s + q.sol, 0);
    acc.push({ mint, amount, buySol, buyTime: lastBuyTime });
    return acc;
  }, []);
}


async function getSymbolByMint(mint, cache, setCache) {
  if (cache[mint]) return cache[mint];
  try {
    const r = await fetch(`/api-auth/coininfo/${encodeURIComponent(mint)}`, {
      credentials: 'include'
    });
    const j = r.ok ? await r.json() : null;
    const symbol = j?.symbol || (mint.slice(0,4) + '...' + mint.slice(-4));
    setCache(prev => ({ ...prev, [mint]: symbol }));
    return symbol;
  } catch {
    const fallback = mint.slice(0,4) + '...' + mint.slice(-4);
    setCache(prev => ({ ...prev, [mint]: fallback }));
    return fallback;
  }
}


async function loadWalletTokens(name) {
  try {
    const [tokensResp, pendingResp] = await Promise.all([
      fetch(`/api-auth/api/wallet-tokens/${encodeURIComponent(name)}`, {
        credentials: 'include'
      }),
      fetch(`/api-auth/api/pending/${encodeURIComponent(name)}`, {
        credentials: 'include'
      })
    ]);

    const tokensJson = await tokensResp.json();
    const pendingJson = pendingResp.ok ? await pendingResp.json() : [];

    const tokens = Array.isArray(tokensJson?.tokens) ? tokensJson.tokens : [];
    const pendingList = Array.isArray(pendingJson) ? pendingJson : [];

    const prepared = await Promise.all(
      tokens.map(async (t) => ({
        mint: t.mint,
        amount: Number(t.amount || 0),
        symbol: await getSymbolByMint(t.mint, symbolByMint, setSymbolByMint)
      }))
    );

    const pendingMap = {};
    for (const item of pendingList) {
      if (item?.mint) pendingMap[item.mint] = true;
    }

    setPositions(prepared);
    setPendingAdded(pendingMap);
  } catch (e) {
    console.error(e);
    setPositions([]);
    setPendingAdded({});
  }
}



async function addToPending(walletName, token) {
  try {
    const r = await fetch('/api-auth/api/pending/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        walletName,
        symbol: token?.symbol || '',
        mint: token?.mint,
        amount: token?.amount
      })
    });
    const j = await r.json();
    if (j?.already) {
      setPendingAdded(prev => ({ ...prev, [token.mint]: true }));
      alert('Токен уже записан в pending');
      return;
    }
    if (j?.added) {
      setPendingAdded(prev => ({ ...prev, [token.mint]: true }));
      alert('Токен добавлен в pending');
      return;
    }
  } catch (e) {
    console.error(e);
    alert('Ошибка добавления в pending');
  }
}


useEffect(() => {
  fetch('api-auth/check', {
    credentials: 'include',
  })
    .then (res => res.json())
    .then (data => setAuthenticated(data.authenticated))
    .catch(() => setAuthenticated(false));
}, []);

const loadWallets = () => {
  fetch('/api/wallets/settings-list')
    .then(res => res.json())
    .then((data) => { 
      setWallets(data);
      updateBalancesOnce(data);
    })
    .catch(err => console.error('Oshibka zagruzki koselkov:', err));
  };

  useEffect(() => {
    loadWallets();
  }, []);


useEffect(() => {
  if (!wallets.length) return;
  const refresh = () => updateBalancesOnce(wallets);
  refresh();
  const id = setInterval(refresh, 12000);
  const onVis = () => {
    if (document.visibilityState === 'visible') refresh();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => {
    clearInterval(id);
    document.removeEventListener('visibilitychange', onVis);
  };
}, [wallets.map(w => w?.walletName).join('|')]);


if (authenticated === null) {
  return (
    <div style={{ color: 'white', textAlign: 'center', marginTop: '100px' }}>
      Zagruzka...
   </div>
  );
}

if (!authenticated) {
  return <LoginPage onLogin={() => setAuthenticated(true)} />;
}

return (
  <Router>
    <div className="App dark bg-gray-900 text-white min-h-screen p-4" style={{ paddingBottom: 80 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '20px 50px 0 50px',
        alignItems: 'center'
      }}>
        <button
          onClick={async () => {
            await fetch('/api-auth/logout', {
              method: 'POST',
              credentials: 'include'
            });
            window.location.href = '/';
          }}
          style={{
            backgroundColor: '#ef4444',
            color: '#000',
            padding: '4px 12px',
            border: 'none',
            borderRadius: '4px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Logout
        </button>
      </div>
      <Routes>
        {/* glavnaja stranica s header */}
        <Route
          path="/"
          element={
            <>
              <header
                style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 50px',
              }}
             >
              <h1 className="text-2xl font-bold ">Aziza</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <select
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  className="bg-gray-800 text-white p-2 rounded"
                >
                  <option value="Solana">Solana</option>
                  <option value="Arbitrum">Arbitrum</option>
                  <option value="Ethereum">Ethereum</option>
                  <option value="BSC">BSC</option>
                </select>


                <Link
                  to="/tasks"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: 'white',
                  }}
                >
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: 'green',
                      marginRight: '8px',
                    }}
                  ></span>
                  <span>Podrobnee</span>
                </Link>
              </div>
            </header>

    {/*Blok s knopkami*/}
    <main className="mt-1">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          maxWidth: '1000px',
          margin: '40px auto',
          padding: '0 20px'
        }}
      >
        <button
          style={{
            justifySelf: 'start',
            padding: '10px 20px',
            backgroundColor: '#facc15',
            border: 'none',
            borderRadius: '10px',
            color: '#1f2937',
            fontWeight: '600',
            fontSize: '16px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            opacity: 1
          }}
          onClick={() => setIsSettingsOpen(true)}
        >
          Create Bot
        </button>


        <Link to="/trading">
          <button style={{
            justifySelf: 'center',
            padding: '6px 12px',
            backgroundColor: '#22c55e',
            border: 'none',
            borderRadius: '4px',
            textDecoration: 'none',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
            Trading
          </button>
        </Link>


        <Link
          to="/traders"
          style={{
            justifySelf: 'end',
            padding: '10px 20px',
            backgroundColor: '#facc15',
            border: 'none',
            borderRadius: '10px',
            textDecoration: 'none',
            color: '#1f2937',
            fontWeight: '600',
            fontSize: '16px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease',
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = '#fbbf24'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = '#facc15'}
        >
          Traders
        </Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center',  margin: '40px auto' }}>
        <table border="1" cellPadding="10" cellSpacing="0" style={{ borderColor: '#333', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#2c2c2c', borderBottom: '1px solid #333', color: '#d1d5db' }}>
            <th>Wallet</th>
            <th>Balans</th>
            <th>Rezerv</th>
            <th>Open</th>
            <th>Open%</th>
            <th>Closed</th>
            <th>Win Rate</th>
            <th>Oshibki</th>
            <th>Status</th>
            <th>Podrobnee</th>
          </tr>
        </thead>
      <tbody>
        {Array.from({ length: 10 }).map((_, i) => {
          const w = wallets[wallets.length - 1 - i] || {};
          return (
            <tr key={i} style={{ color:'#ccc', cursor: w.walletName ? 'pointer' : 'default' }}
              onClick={(e) => {
                if (e.target.closest('button,a,input,textarea,select')) return;
                if (w.walletName) setOpenWallet(w.walletName);
              }}
            >
              <td>{w.walletName || ''}</td>
              <td>{(w.balanceSol ?? null) !== null ? `${Number(w.balanceSol).toFixed(6)} SOL` : '-'}<br />
                  {(w.balanceSol ?? null) !== null && solUsd !== null ? `${(Number(w.balanceSol) * solUsd).toFixed(2)} $` : ''}</td>

              <td>{(w.reserveAmount ?? null) !== null ? `${Number(w.reserveAmount).toFixed(6)} SOL` : '-'}<br />
                  {(w.reserveAmount ?? null) !== null && solUsd !==null ? `${(Number(w.reserveAmount) * solUsd).toFixed(2)} $` : '-'}</td>

                <td>{(w.openTokens ?? null) !== null ? `${Number(w.openTokens).toFixed(6)} SOL` : '-'}<br />
                  {(w.openTokens ?? null) !== null && solUsd !== null ?  `${(Number(w.openTokens) * solUsd).toFixed(2)} $` : '-'}</td>

              <td>{w.openPercent ? `${w.openPercent} %` : ''}</td>
              <td>{(w.closedSol ?? null) !== null ? `${Number(w.closedSol).toFixed(6)} SOL` : '-'}<br />
                  {(w.closedSol ?? null) !== null && solUsd !== null ? `${(Number(w.closedSol) * solUsd).toFixed(2)} $` : '-'}</td>
              <td>{w.winRate ? `${w.winRate} %` : ''}</td>
              <td>{w.errorsCount || ''}</td>
              <td style={{fontWeight:'600'}}>{w.walletName ? <span style={{color:w.running ? '#22c55e' : '#ef4444'}}>{w.running ? 'ON' : 'OFF'}</span> : ''}</td>
              <td>
                  <Link to={`/trading/${w.walletName || ''}`}>
                    <button style={{
                      backgroundColor: '#22c55e',
                      color: '#fff',
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}>
                      Trading
                    </button>
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </main>
 </>
 }
/>


          {/* Stranici */}

          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/traders" element={<TradersPage />} />
          <Route path="/trading" element={<TradingPage solUsd={solUsd} activeRpc={globalRpc} refreshRpc={refreshRpc}/>} />
          <Route path="/trading/:walletName" element={<TradingPage solUsd={solUsd} activeRpc={globalRpc} refreshRpc={refreshRpc}/>} />
        </Routes>

        <SettingsModal 
          isOpen={isSettingsOpen}
          onClose={() => {
            setIsSettingsOpen(false);
            loadWallets();
            updateBalancesOnce(wallets);
          }}
        />
      </div>
  {openWallet && (
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',justifyContent:'center',alignItems:'center',zIndex:1000}} onClick={() => setOpenWallet(null)}>
    <div style={{background:'#1e1e1e',color:'#fff',padding:'16px',borderRadius:'8px',width:'850px'}} onClick={e => e.stopPropagation()}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <b>{openWallet}</b>
        <button onClick={() => setOpenWallet(null)} style={{background:'transparent',color:'#fff',border:'none',fontSize:'18px',cursor:'pointer'}}>✕</button>
      </div>

  <button onClick={() => loadWalletTokens(openWallet)}>
    Load tokens
  </button>

  {Array.isArray(positions) && positions.length === 0 && (
    <div style={{ marginTop: '20px', color: '#aaa' }}>
      No tokens
    </div>
  )}

  {Array.isArray(positions) && positions.length > 0 && (
    <div style={{ marginTop: '16px', display: 'grid', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
      {positions.map((p, i) => (
        <div
          key={p.mint || i}
          style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr 120px 170px',
            gap: '12px',
            alignItems: 'center',
            padding: '10px 12px',
            border: '1px solid #333',
            borderRadius: '8px',
            background: '#111827'
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {p.symbol || 'Unknown'}
          </div>

          <div style={{ color: '#9ca3af', fontSize: '13px', wordBreak: 'break-all' }}>
            {p.mint}
          </div>

          <div style={{ textAlign: 'right' }}>
            {Number(p.amount || 0).toLocaleString(undefined, {
              maximumFractionDigits: 6
            })}
          </div>

          <div style={{ textAlign: 'right' }}>
            <button 
              onClick={() => addToPending(openWallet, p)}
              style={{
                backgroundColor: pendingAdded[p.mint] ? '#22c55e' : '',
                color: pendingAdded[p.mint] ? '#000' : '',
                border: pendingAdded[p.mint] ? 'none' : '',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Add to pending
            </button>
          </div>
        </div>
      ))}
    </div>
  )}
    </div>
  </div>
)}

    </Router>
  );
}

export default App;
